import { create } from 'zustand'
import i18n from '../i18n'
import Cookies from 'js-cookie'
import { setUserDefault } from '../services/userdefaults/userDefaultService'
import { isImpersonating } from './appImpersonationStore'
import { isDemoSession } from '../services/accountSetup/demoSession'

interface AppSettingsState {
  darkMode: boolean
  language: string
  toggleTheme: () => void
  setLanguage: (lang: string) => void
  initializeAccountLanguage: (userId: string | null, language?: string | null) => void
}

export const useAppSettingsStore = create<AppSettingsState>(set => {
  const rootElement = typeof document === 'undefined' ? null : document.documentElement
  const cookieMode = Cookies.get('darkMode')
  const initialDark = cookieMode != null
    ? cookieMode === 'true'
    : rootElement?.classList.contains('dark') ?? false
  rootElement?.classList.toggle('dark', initialDark)

  const cookieLang = Cookies.get('language')
  const linkLanguage = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('lang')
  const initialLang = linkLanguage === 'nl' || linkLanguage === 'en' ? linkLanguage : cookieLang || i18n.language
  i18n.changeLanguage(initialLang)
  if (linkLanguage === 'nl' || linkLanguage === 'en') Cookies.set('language', initialLang, { expires: 365 })
  let languageUserId: string | null = null
  let pendingLanguageSave = Promise.resolve()

  return {
    darkMode: initialDark,
    language: initialLang,

    toggleTheme: () => {
      set(state => {
        const newMode = !state.darkMode
        rootElement?.classList.toggle('dark', newMode)
        Cookies.set('darkMode', String(newMode), { expires: 365 })
        return { darkMode: newMode }
      })
    },

    setLanguage: (lang: string) => {
      i18n.changeLanguage(lang)
      Cookies.set('language', lang, { expires: 365 })
      set({ language: lang })
      const userId = languageUserId
      if (userId && (lang === 'nl' || lang === 'en') && !isImpersonating() && !isDemoSession()) {
        // Keep quick successive choices ordered and bind the write to this login.
        pendingLanguageSave = pendingLanguageSave.then(async () => {
          if (languageUserId !== userId || isImpersonating() || isDemoSession()) return
          await setUserDefault('language', lang, userId)
        }).catch(() => { console.error('Failed to save account language') })
      }
    },

    initializeAccountLanguage: (userId, language) => {
      // Profile polling must not undo a language the customer just selected.
      if (languageUserId === userId) return
      languageUserId = userId
      if (!userId || (language !== 'nl' && language !== 'en')) return
      i18n.changeLanguage(language)
      Cookies.set('language', language, { expires: 365 })
      set({ language })
    },
  }
})
